import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Cpu, Wrench } from "lucide-react";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { formatCost, formatDuration, formatTokens, shortModel, type TraceStep } from "@/service-hooks/use-run-history";

// How an answer or a run was produced, as Settings → Runs & Cost shows it: model calls (tokens, cost), tool calls
// (request / response, expandable) and errors, grouped by part (a deep-analysis specialist, an ontology, setup).

const OPEN_UP_TO = 40; // groups with more steps than this start closed

/** Steps grouped by `stage` (in `order`, then the rest), with a count line; a group with many steps starts closed. */
export default function StepGroups({ title, steps, truncated, order = [], note }: {
  title: string;
  steps: TraceStep[];
  truncated?: boolean;
  order?: string[];
  note?: string;
}) {
  const tools = steps.filter((s) => s.kind === "tool").length;
  const models = steps.filter((s) => s.kind === "model").length;
  const failed = steps.filter((s) => s.ok === false || s.kind === "error").length;
  const staged = steps.some((s) => s.stage);
  const rank = (g: string) => order.indexOf(g) + 1 || 99;
  const groups = staged
    ? [...new Set(steps.map((s) => s.stage ?? "other"))]
        .sort((a, b) => rank(a) - rank(b))
        .map((g) => ({ name: g, steps: steps.filter((s) => (s.stage ?? "other") === g) }))
    : [{ name: "", steps }];
  return (
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-muted mb-2">
        {title}{" "}
        <span className="normal-case">
          · {tools} tool call{tools === 1 ? "" : "s"} · {models} model call{models === 1 ? "" : "s"}
          {failed > 0 && <span className="text-error"> · {failed} failed</span>}
          {truncated && " · trace cut short (too large)"}
          {note && ` · ${note}`}
        </span>
      </p>
      {!steps.length ? (
        <p className="text-xs text-muted">No steps.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((g) => (
            <Group key={g.name || "all"} name={g.name} steps={g.steps} />
          ))}
        </div>
      )}
    </div>
  );
}

function Group({ name, steps }: { name: string; steps: TraceStep[] }) {
  const [open, setOpen] = useState(steps.length <= OPEN_UP_TO);
  const cost = steps.reduce((sum, s) => sum + (s.cost ?? 0), 0);
  const failed = steps.filter((s) => s.ok === false || s.kind === "error").length;
  return (
    <div>
      {name && (
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
          className="flex items-center gap-1.5 text-[11px] font-medium text-white mb-1 hover:text-primary">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          <span className="capitalize">{name}</span>
          <span className="text-muted font-normal">
            · {steps.length} step{steps.length === 1 ? "" : "s"}{cost ? ` · ${formatCost(cost)}` : ""}
          </span>
          {failed > 0 && <span className="text-error font-normal">· {failed} failed</span>}
        </button>
      )}
      {(open || !name) && (
        <div className="flex flex-col border border-surface-border rounded-md divide-y divide-surface-border">
          {steps.map((s, i) => (
            <Step key={`${s.step}-${i}`} step={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function Step({ step }: { step: TraceStep }) {
  const [open, setOpen] = useState(false);
  const bad = step.ok === false || step.kind === "error";
  const canOpen = step.kind !== "model" && Boolean(step.input || step.output || step.error);
  const Icon = step.kind === "tool" ? Wrench : step.kind === "error" ? AlertTriangle : Cpu;
  return (
    <div className="text-xs">
      <button
        type="button"
        className={cn("w-full flex items-center gap-2 px-2 py-1.5 text-left", canOpen ? "hover:bg-surface-hover" : "cursor-default")}
        onClick={() => canOpen && setOpen(!open)}
        aria-expanded={canOpen ? open : undefined}
      >
        <span className="w-6 text-right text-muted tabular-nums">{step.step}</span>
        <Icon className={cn("w-3.5 h-3.5 flex-shrink-0", bad ? "text-error" : step.kind === "tool" ? "text-primary" : "text-muted")} />
        <span className={cn("truncate", bad ? "text-error" : "text-white")}>
          {step.kind === "model"
            ? `Model ${shortModel(step.model ?? null)}${step.toolsAsked?.length ? ` → asked for ${step.toolsAsked.join(", ")}` : ""}${step.stop === "max_tokens" ? " · cut off" : ""}`
            : step.name ?? step.kind}
          {bad && step.error && <span className="text-error"> — {step.error}</span>}
        </span>
        <span className="ml-auto flex items-center gap-3 text-muted tabular-nums whitespace-nowrap">
          {step.kind === "model" && step.inputTokens != null && (
            <span>
              {formatTokens(step.inputTokens)} in · {formatTokens(step.outputTokens ?? 0)} out · {formatCost(step.cost ?? 0)}
            </span>
          )}
          {step.kind === "tool" && step.outputChars != null && <span>{(step.outputChars / 1000).toFixed(0)} KB out</span>}
          {step.ms != null && <span>{formatDuration(step.ms / 1000)}</span>}
          {canOpen && (open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />)}
        </span>
      </button>
      {open && (
        <div className="grid gap-2 px-2 pb-2 lg:grid-cols-2">
          {step.input != null && <StepText label="Request" text={step.input} full={step.inputChars} />}
          {step.output != null && <StepText label="Response" text={step.output} full={step.outputChars} />}
          {step.output == null && step.outputChars != null && (
            <p className="text-[11px] text-muted">Response not kept ({step.outputChars.toLocaleString()} characters; trace too large).</p>
          )}
          {step.kind === "error" && step.error && <StepText label="Error" text={step.error} />}
        </div>
      )}
    </div>
  );
}

function StepText({ label, text, full }: { label: string; text: string; full?: number }) {
  return (
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wide text-muted mb-1">
        {label}
        {full != null && full > text.length && (
          <span className="normal-case"> · first {text.length.toLocaleString()} of {full.toLocaleString()} characters</span>
        )}
      </p>
      <pre className={cn("max-h-[260px] overflow-auto whitespace-pre-wrap break-words bg-surface-active rounded p-2 text-[11px] text-white", CUSTOM_SCROLLBAR)}>
        {pretty(text)}
      </pre>
    </div>
  );
}

const pretty = (text: string): string => {
  const t = text.trim();
  if (!(t.startsWith("{") || t.startsWith("["))) return text;
  try {
    return JSON.stringify(JSON.parse(t), null, 2);
  } catch {
    return text; // cut JSON: as stored
  }
};
